import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import DataTable, { TableColumn } from 'react-data-table-component';
import {
    Shield, Users, Route, Lock, Unlock, Plus, Trash2, Save,
    AlertTriangle, CheckCircle, ChevronDown, ChevronRight, History, Edit2
} from 'lucide-react';
import { getApiBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';

// Types
interface RouteEntry {
    Id: number;
    RoutePath: string;
    Label: string;
    Category: string;
    DefaultAccess: 'all' | 'restricted';
    IsActive: boolean;
    RolePermissions: { RoleId: number; RoleName: string; AccessLevel: string }[];
}

interface Role {
    Id: number;
    Name: string;
    Description: string;
    IsSystem: boolean;
    CreatedAt: string;
    UserCount: number;
    RouteCount: number;
}

interface UserWithRoles {
    email: string;
    name: string;
    roles: { id: number; roleId: number; roleName: string; grantedBy: string; grantedAt: string }[];
}

interface AuditEntry {
    Id: number;
    Action: string;
    TargetType: string;
    TargetId: string;
    Details: string;
    PerformedBy: string;
    PerformedAt: string;
}

interface RoleRouteEntry {
    Id: number;
    RoutePath: string;
    AccessLevel: string;
    Label: string;
    Category: string;
}

const apiUrl = getApiBaseUrl();

const Permissions: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    // Tab state
    const [activeTab, setActiveTab] = useState<'routes' | 'roles' | 'users' | 'audit'>('routes');
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Routes tab
    const [routes, setRoutes] = useState<RouteEntry[]>([]);
    const [routeSearch, setRouteSearch] = useState('');

    // Roles tab
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [roleRoutes, setRoleRoutes] = useState<RoleRouteEntry[]>([]);
    const [allRoutesForRole, setAllRoutesForRole] = useState<RouteEntry[]>([]);
    const [showNewRole, setShowNewRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleDescription, setNewRoleDescription] = useState('');

    // Users tab
    const [users, setUsers] = useState<UserWithRoles[]>([]);
    const [userSearch, setUserSearch] = useState('');
    const [assignEmail, setAssignEmail] = useState('');
    const [assignRoleId, setAssignRoleId] = useState<number | null>(null);
    const [editingUser, setEditingUser] = useState<UserWithRoles | null>(null);
    const [editRoleIds, setEditRoleIds] = useState<Set<number>>(new Set());

    // Audit tab
    const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);

    const getAuthHeader = async () => {
        const token = await acquireTokenSafe(instance, accounts);
        if (!token) return null;
        return `Bearer ${token}`;
    };

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(null), 3000);
    };

    const showError = (msg: string) => {
        setError(msg);
        setTimeout(() => setError(null), 5000);
    };

    // Fetch routes
    const fetchRoutes = async () => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/routes`, { headers: { Authorization: auth } });
            if (res.status === 403) { setHasAccess(false); return; }
            if (!res.ok) throw new Error('Failed to fetch routes');
            setRoutes(await res.json());
            setHasAccess(true);
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Fetch roles
    const fetchRoles = async () => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/roles`, { headers: { Authorization: auth } });
            if (!res.ok) throw new Error('Failed to fetch roles');
            setRoles(await res.json());
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Fetch users
    const fetchUsers = async () => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/users`, { headers: { Authorization: auth } });
            if (!res.ok) throw new Error('Failed to fetch users');
            setUsers(await res.json());
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Fetch audit log
    const fetchAudit = async () => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/audit`, { headers: { Authorization: auth } });
            if (!res.ok) throw new Error('Failed to fetch audit log');
            setAuditLog(await res.json());
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Fetch role routes
    const fetchRoleRoutes = async (roleId: number) => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/roles/${roleId}/routes`, { headers: { Authorization: auth } });
            if (!res.ok) throw new Error('Failed to fetch role routes');
            setRoleRoutes(await res.json());
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Initial load
    useEffect(() => {
        if (accounts.length === 0) { setHasAccess(false); return; }
        setLoading(true);
        Promise.all([fetchRoutes(), fetchRoles(), fetchUsers()]).finally(() => setLoading(false));
    }, [accounts]);

    // Load tab-specific data
    useEffect(() => {
        if (activeTab === 'audit' && auditLog.length === 0) fetchAudit();
    }, [activeTab]);

    // When a role is selected, load its routes
    useEffect(() => {
        if (selectedRole) {
            fetchRoleRoutes(selectedRole.Id);
            setAllRoutesForRole(routes);
        }
    }, [selectedRole]);

    // Toggle route access
    const toggleRouteAccess = async (route: RouteEntry) => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const newAccess = route.DefaultAccess === 'all' ? 'restricted' : 'all';
            const res = await fetch(`${apiUrl}/api/permissions/routes/${route.Id}`, {
                method: 'PUT',
                headers: { Authorization: auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ defaultAccess: newAccess })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess(`${route.Label} set to ${newAccess}`);
            fetchRoutes();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Create role
    const createRole = async () => {
        if (!newRoleName.trim()) return;
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/roles`, {
                method: 'POST',
                headers: { Authorization: auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newRoleName.trim(), description: newRoleDescription.trim() })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess(`Role "${newRoleName.trim()}" created`);
            setNewRoleName('');
            setNewRoleDescription('');
            setShowNewRole(false);
            fetchRoles();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Delete role
    const deleteRole = async (role: Role) => {
        if (!window.confirm(`Delete role "${role.Name}"? This will remove it from all users.`)) return;
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/roles/${role.Id}`, {
                method: 'DELETE', headers: { Authorization: auth }
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess(`Role "${role.Name}" deleted`);
            if (selectedRole?.Id === role.Id) setSelectedRole(null);
            fetchRoles();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Save role routes (bulk)
    const saveRoleRoutes = async (roleId: number, routePaths: string[]) => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/roles/${roleId}/routes`, {
                method: 'PUT',
                headers: { Authorization: auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ routes: routePaths.map(p => ({ routePath: p, accessLevel: 'view' })) })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess('Route permissions saved');
            fetchRoleRoutes(roleId);
            fetchRoles();
            fetchRoutes();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Assign role to user
    const assignRole = async () => {
        if (!assignEmail.trim() || !assignRoleId) return;
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/users/roles`, {
                method: 'POST',
                headers: { Authorization: auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userEmail: assignEmail.trim(), roleId: assignRoleId })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess(`Role assigned to ${assignEmail.trim()}`);
            setAssignEmail('');
            setAssignRoleId(null);
            fetchUsers();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Open edit modal for a user
    const openEditRoles = (user: UserWithRoles) => {
        setEditingUser(user);
        setEditRoleIds(new Set(user.roles.map(r => r.roleId)));
    };

    // Bulk update user roles
    const updateUserRoles = async () => {
        if (!editingUser) return;
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/users/roles`, {
                method: 'PUT',
                headers: { Authorization: auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userEmail: editingUser.email, roleIds: Array.from(editRoleIds) })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess(`Roles updated for ${editingUser.name || editingUser.email}`);
            setEditingUser(null);
            fetchUsers();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Toggle a role in the edit modal
    const toggleEditRole = (roleId: number) => {
        const next = new Set(editRoleIds);
        if (next.has(roleId)) next.delete(roleId);
        else next.add(roleId);
        setEditRoleIds(next);
    };

    // Remove role from user
    const removeUserRole = async (assignmentId: number) => {
        try {
            const auth = await getAuthHeader();
            if (!auth) return;
            const res = await fetch(`${apiUrl}/api/permissions/users/roles/${assignmentId}`, {
                method: 'DELETE', headers: { Authorization: auth }
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
            showSuccess('Role removed');
            fetchUsers();
        } catch (err: any) {
            showError(err.message);
        }
    };

    // Access denied
    if (hasAccess === false) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="header-title-section">
                        <Lock size={28} />
                        <h1>Permissions</h1>
                    </div>
                </div>
                <div className="stats-grid" style={{ marginTop: 24 }}>
                    <div className="stat-card">
                        <AlertTriangle size={48} style={{ color: 'var(--accent-yellow)', marginBottom: 12 }} />
                        <div className="stat-label">Access Denied</div>
                        <div className="stat-sublabel">You do not have permission to manage permissions. Contact an administrator.</div>
                    </div>
                </div>
            </div>
        );
    }

    // Filtered routes
    const filteredRoutes = routes.filter(r =>
        r.Label.toLowerCase().includes(routeSearch.toLowerCase()) ||
        r.RoutePath.toLowerCase().includes(routeSearch.toLowerCase()) ||
        r.Category.toLowerCase().includes(routeSearch.toLowerCase())
    );

    // Group routes by category
    const routesByCategory: Record<string, RouteEntry[]> = {};
    filteredRoutes.forEach(r => {
        if (!routesByCategory[r.Category]) routesByCategory[r.Category] = [];
        routesByCategory[r.Category].push(r);
    });

    // Filtered users
    const filteredUsers = users.filter(u =>
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.name.toLowerCase().includes(userSearch.toLowerCase())
    );

    // Route columns
    const routeColumns: TableColumn<RouteEntry>[] = [
        {
            name: 'Route',
            selector: row => row.Label,
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.Label}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{row.RoutePath}</div>
                </div>
            ),
            sortable: true,
            grow: 2,
        },
        {
            name: 'Category',
            selector: row => row.Category,
            sortable: true,
            width: '140px',
            cell: row => <span className="badge moderate">{row.Category}</span>
        },
        {
            name: 'Access',
            width: '160px',
            cell: row => (
                <button
                    onClick={() => toggleRouteAccess(row)}
                    className={`badge ${row.DefaultAccess === 'all' ? 'renewable' : 'one-off'}`}
                    style={{ cursor: 'pointer', border: 'none', padding: '4px 12px' }}
                    title={`Click to change to ${row.DefaultAccess === 'all' ? 'restricted' : 'all'}`}
                >
                    {row.DefaultAccess === 'all' ? (
                        <><Unlock size={12} style={{ marginRight: 4 }} /> All Users</>
                    ) : (
                        <><Lock size={12} style={{ marginRight: 4 }} /> Restricted</>
                    )}
                </button>
            ),
        },
        {
            name: 'Roles with Access',
            grow: 2,
            cell: row => row.DefaultAccess === 'restricted' && row.RolePermissions.length > 0 ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {row.RolePermissions.map((rp, i) => (
                        <span key={i} className="badge offline" style={{ fontSize: 11 }}>{rp.RoleName}</span>
                    ))}
                </div>
            ) : row.DefaultAccess === 'restricted' ? (
                <span style={{ opacity: 0.5, fontSize: 12 }}>No roles assigned</span>
            ) : (
                <span style={{ opacity: 0.4, fontSize: 12 }}>—</span>
            ),
        },
    ];

    // User columns
    const userColumns: TableColumn<UserWithRoles>[] = [
        {
            name: 'User',
            selector: row => row.name,
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{row.email}</div>
                </div>
            ),
            sortable: true,
            grow: 2,
        },
        {
            name: 'Roles',
            grow: 3,
            cell: row => (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {row.roles.length === 0 && <span style={{ opacity: 0.5, fontSize: 12 }}>No roles</span>}
                    {row.roles.map((r) => (
                        <span key={r.id} className={`badge ${r.roleName === 'Admin' ? 'one-off' : 'renewable'}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {r.roleName}
                            <button
                                onClick={() => removeUserRole(r.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}
                                title={`Remove ${r.roleName} role`}
                            >
                                <Trash2 size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            ),
        },
        {
            name: '',
            width: '60px',
            cell: row => (
                <button
                    onClick={() => openEditRoles(row)}
                    style={{ background: 'none', border: '1px solid var(--border-primary)', borderRadius: 6, cursor: 'pointer', color: 'var(--accent-blue)', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                    title="Edit roles"
                >
                    <Edit2 size={13} />
                </button>
            ),
        },
    ];

    // Audit columns
    const auditColumns: TableColumn<AuditEntry>[] = [
        {
            name: 'Time',
            selector: row => row.PerformedAt,
            cell: row => new Date(row.PerformedAt).toLocaleString(),
            sortable: true,
            width: '180px',
        },
        { name: 'Action', selector: row => row.Action, sortable: true, width: '180px',
            cell: row => <span className="badge offline">{row.Action.replace(/_/g, ' ')}</span>
        },
        { name: 'Target', selector: row => row.TargetId || '', width: '200px' },
        { name: 'Details', selector: row => row.Details || '', grow: 2 },
        { name: 'By', selector: row => row.PerformedBy, width: '200px' },
    ];

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-title-section">
                    <Shield size={28} />
                    <div>
                        <h1>Permissions Management</h1>
                        <p className="page-subtitle">Manage route access, roles, and user permissions</p>
                    </div>
                </div>
            </div>

            {/* Status messages */}
            {successMsg && (
                <div className="stat-card" style={{ background: 'var(--accent-green)', color: '#fff', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle size={16} /> {successMsg}
                </div>
            )}
            {error && (
                <div className="stat-card" style={{ background: 'var(--accent-red)', color: '#fff', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={16} /> {error}
                </div>
            )}

            {/* Tab navigation */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border-primary)' }}>
                {[
                    { key: 'routes' as const, label: 'Routes', icon: <Route size={16} />, count: routes.length },
                    { key: 'roles' as const, label: 'Roles', icon: <Shield size={16} />, count: roles.length },
                    { key: 'users' as const, label: 'Users', icon: <Users size={16} />, count: users.length },
                    { key: 'audit' as const, label: 'Audit Log', icon: <History size={16} /> },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            padding: '10px 20px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === tab.key ? '2px solid var(--accent-blue)' : '2px solid transparent',
                            color: activeTab === tab.key ? 'var(--accent-blue)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontWeight: activeTab === tab.key ? 600 : 400,
                            fontSize: 14,
                            transition: 'all 0.2s',
                        }}
                    >
                        {tab.icon} {tab.label}
                        {tab.count !== undefined && (
                            <span style={{
                                background: 'var(--bg-tertiary)',
                                borderRadius: 10,
                                padding: '1px 8px',
                                fontSize: 11,
                            }}>{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Routes Tab */}
            {activeTab === 'routes' && (
                <div className="card">
                    <div className="card-header">
                        <div className="card-title">
                            <Route size={18} />
                            Route Access Control
                        </div>
                        <input
                            type="text"
                            placeholder="Search routes..."
                            value={routeSearch}
                            onChange={e => setRouteSearch(e.target.value)}
                            className="search-input"
                            style={{ width: 240 }}
                        />
                    </div>
                    <p style={{ padding: '0 20px 8px', fontSize: 13, opacity: 0.6 }}>
                        Click the access badge to toggle between "All Users" (any authenticated user) and "Restricted" (requires role permission).
                    </p>
                    <DataTable
                        columns={routeColumns}
                        data={filteredRoutes}
                        theme="dark"
                        pagination
                        paginationPerPage={20}
                        highlightOnHover
                        dense
                        customStyles={{
                            table: { style: { backgroundColor: 'transparent' } },
                            headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' } },
                            headCells: { style: { color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' } },
                            rows: { style: { backgroundColor: 'transparent', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', '&:hover': { backgroundColor: 'var(--bg-secondary)' } } },
                            cells: { style: { color: 'var(--text-primary)' } },
                            pagination: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-primary)' } },
                        }}
                    />
                </div>
            )}

            {/* Roles Tab */}
            {activeTab === 'roles' && (
                <div style={{ display: 'flex', gap: 20 }}>
                    {/* Left: Role list */}
                    <div className="card" style={{ flex: '0 0 340px' }}>
                        <div className="card-header" style={{ justifyContent: 'space-between' }}>
                            <div className="card-title"><Shield size={18} /> Roles</div>
                            <button
                                onClick={() => setShowNewRole(!showNewRole)}
                                style={{
                                    background: 'var(--accent-blue)', border: 'none', color: '#fff',
                                    borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13
                                }}
                            >
                                <Plus size={14} /> New Role
                            </button>
                        </div>

                        {showNewRole && (
                            <div style={{ padding: '0 16px 16px', borderBottom: '1px solid var(--border-primary)' }}>
                                <input
                                    type="text"
                                    placeholder="Role name"
                                    value={newRoleName}
                                    onChange={e => setNewRoleName(e.target.value)}
                                    className="search-input"
                                    style={{ width: '100%', marginBottom: 8 }}
                                />
                                <input
                                    type="text"
                                    placeholder="Description (optional)"
                                    value={newRoleDescription}
                                    onChange={e => setNewRoleDescription(e.target.value)}
                                    className="search-input"
                                    style={{ width: '100%', marginBottom: 8 }}
                                />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={createRole} style={{ background: 'var(--accent-green)', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
                                        Create
                                    </button>
                                    <button onClick={() => { setShowNewRole(false); setNewRoleName(''); setNewRoleDescription(''); }} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={{ padding: '8px 0' }}>
                            {roles.map(role => (
                                <div
                                    key={role.Id}
                                    onClick={() => setSelectedRole(role)}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderLeft: selectedRole?.Id === role.Id ? '3px solid var(--accent-blue)' : '3px solid transparent',
                                        background: selectedRole?.Id === role.Id ? 'var(--bg-tertiary)' : 'transparent',
                                        transition: 'all 0.15s',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {role.Name}
                                            {role.IsSystem && <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 400 }}>SYSTEM</span>}
                                        </div>
                                        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{role.Description}</div>
                                        <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>
                                            {role.UserCount} user{role.UserCount !== 1 ? 's' : ''} · {role.RouteCount} route{role.RouteCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    {!role.IsSystem && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteRole(role); }}
                                            style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: 4 }}
                                            title="Delete role"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Role route permissions */}
                    <div className="card" style={{ flex: 1 }}>
                        {selectedRole ? (
                            <RoleRouteEditor
                                role={selectedRole}
                                roleRoutes={roleRoutes}
                                allRoutes={allRoutesForRole.length > 0 ? allRoutesForRole : routes}
                                onSave={(paths) => saveRoleRoutes(selectedRole.Id, paths)}
                            />
                        ) : (
                            <div style={{ padding: 40, textAlign: 'center', opacity: 0.5 }}>
                                <Shield size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
                                <div>Select a role to manage its route permissions</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
                <div className="card">
                    <div className="card-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div className="card-title"><Users size={18} /> User Role Assignments</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={userSearch}
                                onChange={e => setUserSearch(e.target.value)}
                                className="search-input"
                                style={{ width: 200 }}
                            />
                        </div>
                    </div>

                    {/* Assign role form */}
                    <div style={{ padding: '0 20px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="email"
                            placeholder="User email"
                            value={assignEmail}
                            onChange={e => setAssignEmail(e.target.value)}
                            className="search-input"
                            style={{ width: 260 }}
                        />
                        <select
                            value={assignRoleId ?? ''}
                            onChange={e => setAssignRoleId(e.target.value ? Number(e.target.value) : null)}
                            className="search-input"
                            style={{ width: 180 }}
                        >
                            <option value="">Select role...</option>
                            {roles.map(r => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                        </select>
                        <button
                            onClick={assignRole}
                            disabled={!assignEmail.trim() || !assignRoleId}
                            style={{
                                background: assignEmail.trim() && assignRoleId ? 'var(--accent-blue)' : 'var(--bg-tertiary)',
                                border: 'none', color: '#fff', borderRadius: 6, padding: '8px 16px',
                                cursor: assignEmail.trim() && assignRoleId ? 'pointer' : 'not-allowed',
                                display: 'flex', alignItems: 'center', gap: 6, fontSize: 13
                            }}
                        >
                            <Plus size={14} /> Assign Role
                        </button>
                    </div>

                    <DataTable
                        columns={userColumns}
                        data={filteredUsers}
                        theme="dark"
                        pagination
                        paginationPerPage={15}
                        highlightOnHover
                        dense
                        customStyles={{
                            table: { style: { backgroundColor: 'transparent' } },
                            headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' } },
                            headCells: { style: { color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' } },
                            rows: { style: { backgroundColor: 'transparent', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', '&:hover': { backgroundColor: 'var(--bg-secondary)' } } },
                            cells: { style: { color: 'var(--text-primary)' } },
                            pagination: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-primary)' } },
                        }}
                    />

                    {/* Edit Roles Modal */}
                    {editingUser && (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setEditingUser(null)}>
                            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 24, minWidth: 380, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                                <h3 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Edit2 size={18} /> Edit Roles
                                </h3>
                                <p style={{ margin: '0 0 16px', fontSize: 13, opacity: 0.6 }}>
                                    {editingUser.name} ({editingUser.email})
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                                    {roles.map(role => (
                                        <label
                                            key={role.Id}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                                                background: editRoleIds.has(role.Id) ? 'var(--bg-tertiary)' : 'transparent',
                                                border: '1px solid',
                                                borderColor: editRoleIds.has(role.Id) ? 'var(--accent-blue)' : 'var(--border-primary)',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={editRoleIds.has(role.Id)}
                                                onChange={() => toggleEditRole(role.Id)}
                                                style={{ accentColor: 'var(--accent-blue)' }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{role.Name}</div>
                                                {role.Description && <div style={{ fontSize: 12, opacity: 0.5 }}>{role.Description}</div>}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setEditingUser(null)}
                                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={updateUserRoles}
                                        style={{ background: 'var(--accent-blue)', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                                    >
                                        <Save size={14} /> Save Roles
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && (
                <div className="card">
                    <div className="card-header">
                        <div className="card-title"><History size={18} /> Permission Change Audit Log</div>
                        <button
                            onClick={fetchAudit}
                            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
                        >
                            Refresh
                        </button>
                    </div>
                    <DataTable
                        columns={auditColumns}
                        data={auditLog}
                        theme="dark"
                        pagination
                        paginationPerPage={20}
                        highlightOnHover
                        dense
                        noDataComponent={<div style={{ padding: 40, opacity: 0.5 }}>No audit entries yet</div>}
                        customStyles={{
                            table: { style: { backgroundColor: 'transparent' } },
                            headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' } },
                            headCells: { style: { color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' } },
                            rows: { style: { backgroundColor: 'transparent', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', '&:hover': { backgroundColor: 'var(--bg-secondary)' } } },
                            cells: { style: { color: 'var(--text-primary)' } },
                            pagination: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-primary)' } },
                        }}
                    />
                </div>
            )}
        </div>
    );
};

// Sub-component: Role Route Editor with checkboxes
interface RoleRouteEditorProps {
    role: Role;
    roleRoutes: RoleRouteEntry[];
    allRoutes: RouteEntry[];
    onSave: (paths: string[]) => void;
}

const RoleRouteEditor: React.FC<RoleRouteEditorProps> = ({ role, roleRoutes, allRoutes, onSave }) => {
    const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [dirty, setDirty] = useState(false);

    // Initialize from roleRoutes
    useEffect(() => {
        setSelectedPaths(new Set(roleRoutes.map(r => r.RoutePath)));
        setDirty(false);
        // Expand all categories that have a selected route
        const cats = new Set<string>();
        roleRoutes.forEach(rr => {
            const route = allRoutes.find(r => r.RoutePath === rr.RoutePath);
            if (route) cats.add(route.Category);
        });
        setExpandedCategories(cats);
    }, [roleRoutes, allRoutes]);

    const restrictedRoutes = allRoutes.filter(r => r.DefaultAccess === 'restricted');
    const byCategory: Record<string, RouteEntry[]> = {};
    restrictedRoutes.forEach(r => {
        if (!byCategory[r.Category]) byCategory[r.Category] = [];
        byCategory[r.Category].push(r);
    });

    const togglePath = (path: string) => {
        const next = new Set(selectedPaths);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        setSelectedPaths(next);
        setDirty(true);
    };

    const toggleCategory = (cat: string) => {
        const next = new Set(expandedCategories);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        setExpandedCategories(next);
    };

    return (
        <>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
                <div className="card-title">
                    <Shield size={18} />
                    {role.Name} — Route Permissions
                </div>
                {dirty && (
                    <button
                        onClick={() => onSave(Array.from(selectedPaths))}
                        style={{
                            background: 'var(--accent-green)', border: 'none', color: '#fff',
                            borderRadius: 6, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13
                        }}
                    >
                        <Save size={14} /> Save Changes
                    </button>
                )}
            </div>
            <p style={{ padding: '0 20px 8px', fontSize: 13, opacity: 0.6 }}>
                Select which restricted routes this role can access. Routes with "All Users" access don't need role assignment.
            </p>
            {restrictedRoutes.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', opacity: 0.5 }}>
                    No restricted routes. All routes are currently set to "All Users".
                </div>
            ) : (
                <div style={{ padding: '0 16px 16px' }}>
                    {Object.entries(byCategory).map(([cat, catRoutes]) => (
                        <div key={cat} style={{ marginBottom: 8 }}>
                            <button
                                onClick={() => toggleCategory(cat)}
                                style={{
                                    background: 'none', border: 'none', color: 'var(--text-primary)',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 4px', fontSize: 13, fontWeight: 600, width: '100%',
                                }}
                            >
                                {expandedCategories.has(cat) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                {cat}
                                <span style={{ fontSize: 11, opacity: 0.5, fontWeight: 400 }}>
                                    ({catRoutes.filter(r => selectedPaths.has(r.RoutePath)).length}/{catRoutes.length})
                                </span>
                            </button>
                            {expandedCategories.has(cat) && (
                                <div style={{ paddingLeft: 24 }}>
                                    {catRoutes.map(route => (
                                        <label
                                            key={route.RoutePath}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '6px 8px', cursor: 'pointer', borderRadius: 4,
                                                background: selectedPaths.has(route.RoutePath) ? 'var(--bg-tertiary)' : 'transparent',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedPaths.has(route.RoutePath)}
                                                onChange={() => togglePath(route.RoutePath)}
                                                style={{ accentColor: 'var(--accent-blue)' }}
                                            />
                                            <span style={{ fontSize: 13 }}>{route.Label}</span>
                                            <span style={{ fontSize: 11, opacity: 0.4, marginLeft: 'auto' }}>{route.RoutePath}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default Permissions;
